import {
  Button,
  Flex,
  Grid,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";

const FAKE_FARMS = [
  { id: "farm-001", name: "Finca Las Palmas" },
  { id: "farm-002", name: "Finca El Roble" },
  { id: "farm-003", name: "Finca La Esperanza" },
  { id: "farm-004", name: "Finca San José" },
  { id: "farm-005", name: "Finca Santa Rita" },
];

const FAKE_LOTS_BY_FARM = {
  "farm-001": [
    { id: "lot-001A", name: "Lote A" },
    { id: "lot-001B", name: "Lote B" },
    { id: "lot-001C", name: "Lote C" },
    { id: "lot-001D", name: "Lote D" },
    { id: "lot-001E", name: "Lote E" },
  ],
  "farm-002": [
    { id: "lot-002A", name: "Lote A" },
    { id: "lot-002B", name: "Lote B" },
    { id: "lot-002C", name: "Lote C" },
    { id: "lot-002D", name: "Lote D" },
    { id: "lot-002E", name: "Lote E" },
  ],
};

const FAKE_CROP_UNITS_BY_LOT = {
  "lot-001A": [
    { id: "cu-001", name: "Árbol 001" },
    { id: "cu-002", name: "Árbol 002" },
    { id: "cu-003", name: "Árbol 003" },
    { id: "cu-004", name: "Árbol 004" },
    { id: "cu-005", name: "Árbol 005" },
  ],
  "lot-002A": [
    { id: "cu-101", name: "Árbol 101" },
    { id: "cu-102", name: "Árbol 102" },
    { id: "cu-103", name: "Árbol 103" },
    { id: "cu-104", name: "Árbol 104" },
    { id: "cu-105", name: "Árbol 105" },
  ],
};

const FAKE_COLLABORATORS = [
  { id: "col-0001", name: "Juan Pérez" },
  { id: "col-0002", name: "María Gómez" },
  { id: "col-0003", name: "Carlos Rojas" },
  { id: "col-0004", name: "Ana Martínez" },
  { id: "col-0005", name: "Luis Díaz" },
];

const STATE_OPTIONS = ["", "Semilla", "Injerto", "Reinjertar", "Crecimiento"];

const GraftControlForm = ({
  onSubmit,
  type = "create",
  initialValues = {},
  onCancel,
}) => {
  const defaultState = {
    date: "",
    performedById: "",
    performedByName: "",
    farmId: "",
    farmName: "",
    lotId: "",
    lotName: "",
    cropUnitId: "",
    cropUnitName: "",
    currentState: "",
    newState: "",
    photoEvidence: "",
    importantNotes: "",
    createdAt: "",
  };

  const [form, setForm] = useState({ ...defaultState, ...initialValues });
  const [availableLots, setAvailableLots] = useState([]);
  const [availableCropUnits, setAvailableCropUnits] = useState([]);

  useEffect(() => {
    setForm({ ...defaultState, ...initialValues });

    if (initialValues.farmId) {
      const lots = FAKE_LOTS_BY_FARM[initialValues.farmId] || [];
      setAvailableLots(lots);
      if (initialValues.lotId && !initialValues.lotName) {
        const foundLot = lots.find((l) => l.id === initialValues.lotId);
        if (foundLot) setForm((f) => ({ ...f, lotName: foundLot.name }));
      }
    }

    if (initialValues.lotId) {
      const cus = FAKE_CROP_UNITS_BY_LOT[initialValues.lotId] || [];
      setAvailableCropUnits(cus);
      if (initialValues.cropUnitId && !initialValues.cropUnitName) {
        const found = cus.find((c) => c.id === initialValues.cropUnitId);
        if (found) setForm((f) => ({ ...f, cropUnitName: found.name }));
      }
    }

    if (initialValues.performedById && !initialValues.performedByName) {
      const found = FAKE_COLLABORATORS.find(
        (c) => c.id === initialValues.performedById
      );
      if (found) setForm((f) => ({ ...f, performedByName: found.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleFarmChange = (farmId) => {
    const farm = FAKE_FARMS.find((f) => f.id === farmId);
    const lots = FAKE_LOTS_BY_FARM[farmId] || [];
    setAvailableLots(lots);
    setAvailableCropUnits([]);
    setForm({
      ...form,
      farmId,
      farmName: farm ? farm.name : "",
      lotId: "",
      lotName: "",
      cropUnitId: "",
      cropUnitName: "",
    });
  };

  const handleLotChange = (lotId) => {
    const lot = (availableLots || []).find((l) => l.id === lotId);
    const cus = FAKE_CROP_UNITS_BY_LOT[lotId] || [];
    setAvailableCropUnits(cus);
    setForm({
      ...form,
      lotId,
      lotName: lot ? lot.name : "",
      cropUnitId: "",
      cropUnitName: "",
    });
  };

  const handleCropUnitChange = (cropUnitId) => {
    const cu = (availableCropUnits || []).find((c) => c.id === cropUnitId);
    setForm({ ...form, cropUnitId, cropUnitName: cu ? cu.name : "" });
  };

  const onSubmitForm = () => {
    const payload = { ...form };
    if (!payload.createdAt) payload.createdAt = new Date().toISOString();
    onSubmit && onSubmit(payload);
  };

  return (
    <Grid rowGap="12px" columnGap="12px" padding="20px">
      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="date"
          label={strings.fields.date.label}
          placeholder="YYYY-MM-DD"
          value={form.date || ""}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
        />
        <SelectField
          name="performedById"
          label={strings.fields.performedById.label}
          value={form.performedById || ""}
          onChange={(e) => {
            const val = e.target.value;
            const found = FAKE_COLLABORATORS.find((c) => c.id === val);
            setForm({
              ...form,
              performedById: val,
              performedByName: found ? found.name : "",
            });
          }}
        >
          <option value="">--</option>
          {FAKE_COLLABORATORS.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="farmId"
          label={strings.fields.farmId.label}
          value={form.farmId || ""}
          onChange={(e) => handleFarmChange(e.target.value)}
        >
          <option value="">--</option>
          {FAKE_FARMS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          name="lotId"
          label={strings.fields.lotId.label}
          value={form.lotId || ""}
          onChange={(e) => handleLotChange(e.target.value)}
        >
          <option value="">--</option>
          {availableLots.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="cropUnitId"
          label={strings.fields.cropUnitId.label}
          value={form.cropUnitId || ""}
          onChange={(e) => handleCropUnitChange(e.target.value)}
        >
          <option value="">--</option>
          {availableCropUnits.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          name="currentState"
          label={strings.fields.currentState.label}
          value={form.currentState || ""}
          onChange={(e) => setForm({ ...form, currentState: e.target.value })}
        >
          {STATE_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s || "--"}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="newState"
          label={strings.fields.newState.label}
          value={form.newState || ""}
          onChange={(e) => setForm({ ...form, newState: e.target.value })}
        >
          {STATE_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s || "--"}
            </option>
          ))}
        </SelectField>

        <TextField
          name="photoEvidence"
          label={strings.fields.photoEvidence.label}
          placeholder="foto1.jpg, foto2.jpg"
          value={
            Array.isArray(form.photoEvidence)
              ? form.photoEvidence.join(", ")
              : form.photoEvidence || ""
          }
          onChange={(e) => setForm({ ...form, photoEvidence: e.target.value })}
        />
      </Grid>

      <TextAreaField
        name="importantNotes"
        label={strings.fields.importantNotes.label}
        value={form.importantNotes || ""}
        onChange={(e) => setForm({ ...form, importantNotes: e.target.value })}
      />

      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={() => setForm(defaultState)}
        />
        <Button
          children={
            type === "create"
              ? strings.fields.buttons.create
              : strings.fields.buttons.save
          }
          onClick={onSubmitForm}
          variation="primary"
        />
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      date: { label: "Fecha" },
      performedById: { label: "Persona que realiza" },
      farmId: { label: "Finca" },
      lotId: { label: "Lote" },
      cropUnitId: { label: "Unidad de cultivo" },
      currentState: { label: "Estado actual" },
      newState: { label: "Nuevo estado" },
      photoEvidence: { label: "Evidencia fotográfica" },
      importantNotes: { label: "Anotaciones importantes" },
      createdAt: { label: "Fecha de creación" },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
        cancel: "Cancelar",
      },
    },
  },
});

export default GraftControlForm;
